import React, { useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModuleNav, ModuleNavItem } from '../../contexts/ModuleNavContext';
import { useMenu } from '../../auth/useMenu';
import { isFieldDevice } from '../../utils/platform';

const ALL_NUTRITION_NAV_ITEMS: ModuleNavItem[] = [
  { to: '/nutrition',           labelKey: 'nutrition.nav.overview',  subject: 'fertilizing:overview',  group: 'nav.groups.overview', end: true },
  // F1: the Programs and Sessions destinations collapse into one workspace
  // whose tabs are routes. Subject is 'fertilizing:sessions' because the entry
  // lands on the rounds board; the fallback below keeps the entry for a user who
  // holds 'fertilizing:programme' without 'fertilizing:sessions', or
  // 'fertilizing:signoff' alone without either of those (F3) — mirroring
  // soil's chain below.
  { to: '/nutrition/foliar', labelKey: 'nutrition.foliar.workspace.title', subject: 'fertilizing:sessions', group: 'nav.groups.operations' },
  // U1: the four soil destinations collapse into one workspace whose tabs are
  // routes. Subject is 'fertilizing:sessions' because the entry lands on the
  // board; the fallback chain below keeps the entry for a user who holds
  // 'fertilizing:programme' without 'fertilizing:sessions' (routes to Program
  // setup), or 'fertilizing:signoff' alone without either of those (routes to
  // the sign-off queue) — e.g. a manager who signs off but does not run
  // sessions or own the programme.
  { to: '/nutrition/soil', labelKey: 'nutrition.soil.workspace.title', subject: 'fertilizing:sessions', group: 'nav.groups.operations' },
  { to: '/nutrition/products',  labelKey: 'nutrition.nav.products',  subject: 'fertilizing:products',  group: 'nav.groups.resources' },
  { to: '/nutrition/inventory', labelKey: 'nutrition.nav.inventory', subject: 'fertilizing:inventory', group: 'nav.groups.resources' },
  { to: '/nutrition/audit',     labelKey: 'nutrition.nav.audit',     subject: 'fertilizing:audit',     group: 'nav.groups.reports' },
  { to: '/nutrition/data-transfer', labelKey: 'nutrition.nav.dataTransfer', subject: 'fertilizing:csvImport', group: 'nav.groups.resources' },
];

const MY_SESSIONS_NAV_ITEM: ModuleNavItem = {
  to: '/nutrition/my-sessions',
  labelKey: 'nutrition.nav.mySessions',
  subject: 'fertilizing:mysessions',
  group: 'nav.groups.operations',
};

// Operator's in-field worklist for the NEW soil group lifecycle (#417 Slice
// 4). Gated on isFieldDevice() (touch-first, tablets included) rather than
// isCompactDevice()'s <=640px, like MY_SESSIONS_NAV_ITEM, and deliberately
// reuses 'fertilizing:mysessions' rather than adding a new RBAC key.
const MY_SOIL_GROUPS_NAV_ITEM: ModuleNavItem = {
  to: '/nutrition/soil-groups',
  labelKey: 'nutrition.nav.mySoilGroups',
  subject: 'fertilizing:mysessions',
  group: 'nav.groups.operations',
};

const NutritionLayout: React.FC = () => {
  const { t } = useTranslation();
  const { setModule } = useModuleNav();

  const hasOverview = useMenu('fertilizing:overview');
  const hasProgramme = useMenu('fertilizing:programme');
  const hasSessions = useMenu('fertilizing:sessions');
  const hasProducts = useMenu('fertilizing:products');
  const hasAudit = useMenu('fertilizing:audit');
  const hasInventory = useMenu('fertilizing:inventory');
  const hasMySessions = useMenu('fertilizing:mysessions');
  const hasCsvImport = useMenu('fertilizing:csvImport');
  const hasSignoff = useMenu('fertilizing:signoff');

  const filteredItems = useMemo(() => {
    const baseItems: ModuleNavItem[] = [
      ...ALL_NUTRITION_NAV_ITEMS,
      ...(isFieldDevice() ? [MY_SESSIONS_NAV_ITEM, MY_SOIL_GROUPS_NAV_ITEM] : []),
    ];
    const menuMap: Record<string, boolean> = {
      'fertilizing:overview': hasOverview,
      'fertilizing:programme': hasProgramme,
      'fertilizing:sessions': hasSessions,
      'fertilizing:products': hasProducts,
      'fertilizing:audit': hasAudit,
      'fertilizing:inventory': hasInventory,
      'fertilizing:mysessions': hasMySessions,
      'fertilizing:csvImport': hasCsvImport,
      'fertilizing:signoff': hasSignoff,
    };
    const soilTarget = hasSessions ? null
      : hasProgramme ? { subject: 'fertilizing:programme', to: '/nutrition/soil/setup' }
      : hasSignoff   ? { subject: 'fertilizing:signoff',   to: '/nutrition/soil/sign-off' }
      : null;
    const foliarTarget = hasSessions ? null
      : hasProgramme ? { subject: 'fertilizing:programme', to: '/nutrition/foliar/setup' }
      : hasSignoff   ? { subject: 'fertilizing:signoff',   to: '/nutrition/foliar/sign-off' }
      : null;
    const items = baseItems.map((item) => {
      if (item.to === '/nutrition/soil' && soilTarget) return { ...item, ...soilTarget };
      if (item.to === '/nutrition/foliar' && foliarTarget) return { ...item, ...foliarTarget };
      return item;
    });
    return items.filter(item => {
      if (!item.subject) return true;
      return menuMap[item.subject] === true;
    });
  }, [hasOverview, hasProgramme, hasSessions, hasProducts, hasAudit, hasInventory, hasMySessions, hasCsvImport, hasSignoff]);

  useEffect(() => {
    setModule(t('nutrition.title'), filteredItems);
    return () => setModule(null, null);
  }, [setModule, t, filteredItems]);

  return (
    <div data-testid="nutrition-layout">
      <Outlet />
    </div>
  );
};

export default NutritionLayout;
